import { useState, useEffect, useCallback } from "react";
import _ from "lodash";
import client from "lib/apolloClient/client";
import { createBulkAiMessagesMutation } from "routes/routes/Teacher/routes/AIPlayground/modules/AIPlaygroundMutations";
import {
  getAiConversationDetailsQuery,
  getAiConversationsQuery,
  getCmsAiToolsQuery,
} from "routes/routes/Teacher/routes/AIPlayground/modules/AIPlaygroundQueries";

const PENDING_IMPORT_KEY = "aiPlayground_pendingImportData";

const hasPendingImport = () => {
  const pendingImportDataStr = localStorage.getItem(PENDING_IMPORT_KEY);
  return !_.isEmpty(pendingImportDataStr);
};

const useImportFromUsageMetrics = ({
  userId,
  createAIConversation,
  onConversationCreated,
}) => {
  const [isImporting, setIsImporting] = useState(hasPendingImport);
  const [importedConversationId, setImportedConversationId] = useState(null);

  const handlePendingImport = useCallback(async () => {
    const pendingImportDataStr = localStorage.getItem(PENDING_IMPORT_KEY);

    if (_.isEmpty(pendingImportDataStr)) {
      setIsImporting(false);
      return;
    }

    if (_.isEmpty(userId)) {
      return;
    }

    let pendingImportData;
    try {
      pendingImportData = JSON.parse(pendingImportDataStr);
    } catch {
      localStorage.removeItem(PENDING_IMPORT_KEY);
      setIsImporting(false);
      return;
    }

    const {
      usageMetricsId,
      entityType,
      conversationDetails: importedDetails,
      messages,
    } = pendingImportData;

    try {
      setIsImporting(true);
      localStorage.removeItem(PENDING_IMPORT_KEY);

      const cmsToolsResponse = await client.query({
        query: getCmsAiToolsQuery,
        fetchPolicy: "cache-first",
      });

      const cmsAiTools = _.get(cmsToolsResponse, "data.cms.aiTools", []);
      const availableToolKeys = _.map(cmsAiTools, cmsTool => {
        const name = _.get(cmsTool, "name", "");
        const version = _.get(cmsTool, "version", "");
        return version ? `${name}_v${version}` : name;
      });
      const importedTools = _.get(importedDetails, "tools", []);
      const validatedTools = _.filter(importedTools, tool =>
        _.includes(availableToolKeys, tool)
      );
      const formattedEntityType = entityType
        ? _.startCase(_.toLower(entityType))
        : "Debug Mode";

      const conversationResponse = await createAIConversation({
        input: {
          entityId: userId,
          entityType: "USER",
          metadata: {
            label: `Imported from ${formattedEntityType} (UsageMetricsId: ${usageMetricsId})`,
            model: _.get(importedDetails, "model", "openai:gpt-4o"),
            temperature: _.get(importedDetails, "temperature", 0.7),
            systemPrompt: _.get(importedDetails, "systemPrompt", ""),
            tools: validatedTools,
          },
        },
      });

      const conversationId = _.get(
        conversationResponse,
        "data.ai.createAiConversation.response.id"
      );

      if (!conversationId) {
        setIsImporting(false);
        return;
      }

      if (!_.isEmpty(messages)) {
        const messagesInput = _.map(messages, msg => ({
          authorType: _.get(msg, "authorType", "user"),
          content: _.get(msg, "content", ""),
          usageMetricsId: _.get(msg, "metadata.usageMetricsId", null),
          metadata: {
            ..._.get(msg, "metadata", {}),
            importedFromUsageMetricsId: usageMetricsId,
          },
        }));

        await client.mutate({
          mutation: createBulkAiMessagesMutation,
          variables: {
            input: {
              conversationId,
              messages: messagesInput,
            },
          },
          awaitRefetchQueries: true,
          refetchQueries: [
            {
              query: getAiConversationDetailsQuery,
              variables: { id: conversationId },
            },
            {
              query: getAiConversationsQuery,
              variables: {
                filters: {
                  entityId: userId,
                  entityType: "USER",
                },
              },
            },
          ],
        });
      }

      setImportedConversationId(conversationId);

      if (onConversationCreated) {
        onConversationCreated(conversationId, importedDetails);
      }

      setIsImporting(false);
    } catch {
      setIsImporting(false);
    }
  }, [userId, createAIConversation, onConversationCreated]);

  useEffect(() => {
    handlePendingImport();
  }, [handlePendingImport]);

  return {
    isImporting,
    importedConversationId,
  };
};

export default useImportFromUsageMetrics;
